/**
 * Mobile / tablet menu drawer — see snippets/mobile-menu-drawer.liquid.
 *
 * Written as a custom element so it re-initialises by itself when the Theme
 * Editor swaps the header section's HTML, and so it has no dependency on the
 * theme's side-drawer component (which only renders under certain settings).
 *
 * Any element with [data-mobile-menu-open] opens it — the click is delegated
 * from the document, so openers that are re-rendered later (the header and Big
 * Header hamburgers) keep working without re-binding.
 */
class MobileMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.opener = null;
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.body.classList.remove('mobile-menu-drawer-open');
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  onDocumentClick(event) {
    const opener = event.target.closest('[data-mobile-menu-open]');
    if (opener) {
      event.preventDefault();
      this.open(opener);
      return;
    }

    if (!this.isOpen || !this.contains(event.target)) return;

    // Closes on the X and on the overlay.
    if (event.target.closest('[data-mobile-menu-close]')) {
      this.close();
      return;
    }

    // Closes on menu links too, so the drawer is never left open over the page
    // it just navigated to. The language picker is excluded: its items are
    // href="#" links handled by localization-form.js, and closing on those would
    // yank the drawer away mid-interaction.
    if (event.target.closest('a[href]') && !event.target.closest('.mobile-menu-drawer__localization')) {
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) this.close();
  }

  open(opener) {
    if (this.isOpen) return;

    this.opener = opener || null;
    this.setAttribute('open', '');
    document.body.classList.add('mobile-menu-drawer-open');
    this.opener?.setAttribute('aria-expanded', 'true');

    // Focus the panel rather than the first link, so the drawer is announced
    // without scrolling a long menu.
    this.querySelector('.mobile-menu-drawer__panel')?.focus({ preventScroll: true });
  }

  close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    document.body.classList.remove('mobile-menu-drawer-open');
    this.opener?.setAttribute('aria-expanded', 'false');
    this.opener?.focus({ preventScroll: true });
    this.opener = null;
  }
}

if (!customElements.get('mobile-menu-drawer')) {
  customElements.define('mobile-menu-drawer', MobileMenuDrawer);
}
